import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as classes from './Form.module.css';
import Button from '../button/Button';
import Title from '../title/Title';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { BLOCKS } from '@contentful/rich-text-types';
import emailjs from 'emailjs-com';
import { API_PATH } from '../../utils/constants'

const Form = ({
  name,
  title,
  fields,
  action,
  successfulSending,
  failedSending,
  marketing,
  mailingLists,
  emailProps,
}) => {
  const [fieldValues, changeFieldValues] = useState({});
  const [formElements, setFormElements] = useState();
  const [formSent, setFormSent] = useState(false);
  const [formFailed, setFormFailed] = useState(false);
  const [successfulMessage, setSuccessfulMessage] = useState('');
  const [failedMessage, setFailedMessage] = useState('');

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (formFailed === true) {
      setFormFailed(false);
    }
    // KLAVIYO logic (marketing)
    const klaviyoData = {
      mailing_lists: mailingLists,
    };
    if (fieldValues.marketing) {
      const year = new Date().getFullYear();
      let month = new Date().getMonth() + 1;
      let day = new Date().getDate();
      if (month.length < 2) {
        month = '0' + month;
      }
      if (day.length < 2) {
        day = '0' + day;
      }
      klaviyoData.email = fieldValues.email;

      let country;
      const getCountryCode = async () => {
        const response = await fetch(`${API_PATH}/region`, { method: 'get' });
        const countryToken = response.json();
        return countryToken;
      };
      getCountryCode().then((result) => {
        const token = result.token.split('.');
        country = JSON.parse(window.atob(token[1]));
      });
      klaviyoData.country = country;

      const submitKlaviyoData = async () => {
        await fetch(`${API_PATH}/customer-create`, {
          method: 'post',
          body: JSON.stringify(klaviyoData),
        });
      };
      submitKlaviyoData();
    }

    //EMAILSJS logic
    const referenceNumber = `${new Date().getDate()}${
      new Date().getMonth() + 1
    }${new Date().getFullYear()}_${new Date().getHours()}${new Date().getMinutes()}`;

    const templateParams = { referenceNumber, marketing: false };
    for (const key in fieldValues) {
      if (Array.isArray(fieldValues[key])) {
        const arrayValue = fieldValues[key].map((item) => {
          return document.body.querySelector(`[name="${item}"]`).parentNode
            .textContent;
        });
        templateParams[key] = arrayValue;
      } else if (
        typeof fieldValues[key] === 'object' &&
        fieldValues[key] !== null
      ) {
        let objectValue = {};
        for (const item in fieldValues[key]) {
          const nameWithSpacesAndCapitals = item
            .slice(0, -8)
            .replace(/^\w/, (c) => c.toUpperCase())
            .replace(/([A-Z])/g, ' $1')
            .trim();
          objectValue[nameWithSpacesAndCapitals] = fieldValues[key][item];
        }
        let objectValueHTML = ``;
        for (const property in objectValue) {
          objectValueHTML += `
            <p>
              ${property}: ${objectValue[property]}
            </p>`;
        }
        templateParams[key] = objectValueHTML;
      } else {
        templateParams[key] = fieldValues[key];
      }
    }

    emailjs
      .send(
        emailProps.serviceId,
        emailProps.templateId,
        templateParams,
        emailProps.userId
      )
      .then(
        (result) => {
          setFormSent(true);
        },
        (error) => {
          setFormFailed(true);
        }
      );
  };

  const handleRemove = useCallback((oldAdditionalInfo, eliminatedOption) => {
    const newAdditionalInfo = { ...oldAdditionalInfo };
    delete newAdditionalInfo[`${eliminatedOption}Textarea`];
    return newAdditionalInfo;
  }, []);

  const handleFieldChange = useCallback(
    (ev) => {
      if (ev.target.type === 'checkbox' && ev.target.name !== 'marketing') {
        const listName = ev.target.id.slice(0, ev.target.id.indexOf('_'));
        if (ev.target.checked) {
          changeFieldValues((oldFieldValues) => ({
            ...oldFieldValues,
            [listName]: [...oldFieldValues[listName], ev.target.name],
          }));
          ev.target.parentNode.className = `${classes.selected} ${classes.label}`;
          if (ev.target.parentNode.parentNode.childNodes[1]) {
            ev.target.parentNode.parentNode.childNodes[1].className = `${classes.visibleTextarea}`;
          }
        } else {
          changeFieldValues((oldFieldValues) => ({
            ...oldFieldValues,
            [listName]: [...oldFieldValues[listName]].filter((element) => {
              return element !== ev.target.name;
            }),
            additionalInfo: handleRemove(
              oldFieldValues.additionalInfo,
              ev.target.name
            ),
          }));
          ev.target.parentNode.className = `${classes.unselected} ${classes.label}`;
          if (ev.target.parentNode.parentNode.childNodes[1]) {
            ev.target.parentNode.parentNode.childNodes[1].className = `${classes.invisibleTextarea}`;
            if (ev.target.parentNode.parentNode.childNodes[1].value !== '') {
              ev.target.parentNode.parentNode.childNodes[1].value = '';
            }
          }
        }
      } else if (
        ev.target.type === 'checkbox' &&
        ev.target.name === 'marketing'
      ) {
        changeFieldValues((oldFieldValues) => ({
          ...oldFieldValues,
          marketing: ev.target.checked,
        }));
      } else if (ev.target.type === 'textarea') {
        changeFieldValues((oldFieldValues) => ({
          ...oldFieldValues,
          additionalInfo: {
            ...oldFieldValues.additionalInfo,
            [ev.target.name]: ev.target.value,
          },
        }));
      } else {
        changeFieldValues((oldFieldValues) => ({
          ...oldFieldValues,
          [ev.target.name]: ev.target.value,
        }));
      }
    },
    [handleRemove]
  );

  useEffect(() => {
    const options = {
      renderNode: {
        [BLOCKS.HEADING_3]: (node, children) => {
          return <h3>{children}</h3>;
        },
        [BLOCKS.PARAGRAPH]: (node, children) => {
          return <p>{children}</p>;
        },
        [BLOCKS.EMBEDDED_ENTRY]: (node) => {
          const componentProps = node.data.target;
          const component = componentProps && componentProps.internal.type;
          if (component === 'ContentfulFormField') {
            changeFieldValues((oldFieldValues) => ({
              ...oldFieldValues,
              [componentProps.name]: componentProps.value,
            }));
            return (
              <div className={classes.inputGroup} key={componentProps.name}>
                <label className={classes.label}>
                  {componentProps.headline}
                  {componentProps.required ? ' *' : ''}
                </label>
                <input
                  type={
                    componentProps.name.includes('date')
                      ? 'datetime-local'
                      : 'text'
                  }
                  id={componentProps.name}
                  name={componentProps.name}
                  placeholder={componentProps.placeholderText}
                  value={fieldValues[componentProps.name]}
                  onChange={handleFieldChange}
                  required={componentProps.required}
                />
              </div>
            );
          } else if (component === 'ContentfulFormOptionList') {
            changeFieldValues((oldFieldValues) => ({
              ...oldFieldValues,
              [componentProps.name]: [],
            }));
            return (
              <div className={classes.optionGroup} key={componentProps.name}>
                <h4>
                  {componentProps.headline}
                  {componentProps.required ? ' *' : ''}
                </h4>
                {componentProps.options &&
                  componentProps.options.map((option, index) => {
                    return (
                      <div className={classes.option} key={option.name}>
                        <label
                          className={`${classes.unselected} ${classes.label}`}
                        >
                          <input
                            type="checkbox"
                            id={`${componentProps.name}_${index}`}
                            name={option.name}
                            value={option.name}
                            onChange={handleFieldChange}
                          />
                          {option.title}
                        </label>
                        {option.extraField ? (
                          <textarea
                            className={classes.invisibleTextarea}
                            name={`${option.name}Textarea`}
                            placeholder={option.placeholderText}
                            onChange={handleFieldChange}
                          />
                        ) : (
                          ''
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          }
        },
      },
    };

    if (fields && !formElements) {
      setFormElements(renderRichText(fields, options));
    }
    if (successfulMessage === '' || failedMessage === '') {
      setSuccessfulMessage(renderRichText(successfulSending, options));
      setFailedMessage(renderRichText(failedSending, options));
    }
  }, [
    fields,
    formElements,
    setFormElements,
    fieldValues,
    successfulMessage,
    successfulSending,
    failedMessage,
    failedSending,
    handleFieldChange,
  ]);

  return formSent ? (
    <div className={classes.formSent}>
      {successfulMessage && successfulMessage}
    </div>
  ) : (
    <>
      <Title title={title} center />
      <form className={classes.bookingsForm} onSubmit={onSubmit}>
        {formElements && formElements}
        {marketing && (
          <div className={classes.marketingSection}>
            <input
              type="checkbox"
              name="marketing"
              onChange={handleFieldChange}
            />
            <label className={classes.marketingLabel}>{marketing}</label>
          </div>
        )}
        <div className={classes.bottomSection}>
          {action.label && <Button primary>{action.label}</Button>}
          {formFailed ? (
            <div className={classes.formFailed}>
              {failedMessage && failedMessage}
            </div>
          ) : (
            ''
          )}
        </div>
      </form>
    </>
  );
};

Form.propTypes = {
  name: PropTypes.string,
  title: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  action: PropTypes.object.isRequired,
  successfulSending: PropTypes.object.isRequired,
  failedSending: PropTypes.object.isRequired,
  marketing: PropTypes.string.isRequired,
  mailingLists: PropTypes.array.isRequired,
  emailProps: PropTypes.object.isRequired,
};

export default Form;
